import type { PropertySetItemProperty } from './PropertySetItem';
import type {
  DisplayEntitySets,
  DisplaySet,
  SplitDisplaySet,
  SplitDisplaySetsResult,
} from './object-properties.types';

/**
 * Locale-aware property/set name comparison used by alphabetical sorting in
 * the object properties panel (matches tcweb numeric + base sensitivity).
 */
export const comparePropertyNames = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

/**
 * Maps names to unique keys by prefixing duplicates with `_`.
 * Matches `@fulcrum/cdm-components` `safePrefixDuplicates` used by tcweb property panels.
 */
export const safePrefixDuplicates = (input: readonly string[]): string[] => {
  let safeReplacer = '-';
  while (input.includes(safeReplacer)) {
    safeReplacer = `_${safeReplacer}`;
  }
  const safeInput = input.map((value) => (value ? value : safeReplacer));
  const originals = new Set(safeInput);
  const resultSet = new Set<string>();
  const result: string[] = [];

  for (const original of safeInput) {
    let value = original;
    while (resultSet.has(value)) {
      value = `_${value}`;
      while (originals.has(value)) {
        value = `_${value}`;
      }
    }
    resultSet.add(value);
    result.push(value);
  }

  return result;
};

/** Encodes a model property favorite key (matches @fulcrum/cdm-components `encodeModelPropertyId`). */
export function encodeModelPropertyFavoriteKey(
  setName: string,
  propertyName: string
): string {
  return `frn:modelprop:${encodeURIComponent(setName)}:${encodeURIComponent(
    propertyName
  )}`;
}

/** Encodes a custom pset property favorite key (matches tcweb/CDM `encodeServicePropertyId`). */
export function encodeCustomPropertyFavoriteKey(
  libId: string,
  defId: string,
  propId: string
): string {
  return `frn:lib:${encodeURIComponent(libId)}/def:${encodeURIComponent(
    defId
  )}/prop:${encodeURIComponent(propId)}`;
}

export interface ToDisplaySetParams {
  psetName: string;
  setKey?: string;
  isCustom?: boolean;
  properties: readonly PropertySetItemProperty[];
  formatName(name: string): string;
  sortPropertiesByName(
    properties: readonly PropertySetItemProperty[]
  ): PropertySetItemProperty[];
}

export interface NormalizedPanelModel {
  favoriteSets: SplitDisplaySet[];
  favoriteSetKeys: string[];
  regularSets: DisplaySet[];
  regularSetKeys: string[];
}

export const toDisplaySet = (params: ToDisplaySetParams): DisplaySet => {
  const key = params.setKey ?? params.psetName;
  return {
    key,
    name: params.formatName(params.psetName),
    isCustom: params.isCustom ?? false,
    properties: params.sortPropertiesByName(params.properties),
  };
};

export const splitSetsByFavorites = (
  sets: readonly DisplaySet[],
  favoriteKeys: ReadonlySet<string>
): SplitDisplaySetsResult => {
  const favorites: SplitDisplaySet[] = [];
  const regular: DisplaySet[] = [];

  for (const set of sets) {
    const favoriteProperties: PropertySetItemProperty[] = [];
    const nonFavoriteProperties: PropertySetItemProperty[] = [];

    for (const property of set.properties) {
      if (favoriteKeys.has(property.key)) {
        favoriteProperties.push(property);
      } else {
        nonFavoriteProperties.push(property);
      }
    }

    if (favoriteProperties.length > 0) {
      favorites.push({
        key: set.key,
        name: set.name,
        favoriteProperties,
        nonFavoriteProperties,
      });
      continue;
    }

    if (set.properties.length > 0) {
      regular.push({
        key: set.key,
        name: set.name,
        isCustom: set.isCustom,
        properties: set.properties,
      });
    }
  }

  return { favorites, regular };
};

const mergeUniqueProperties = (
  properties: readonly PropertySetItemProperty[]
): PropertySetItemProperty[] => {
  const byKey = new Map<string, PropertySetItemProperty>();
  for (const property of properties) {
    if (!byKey.has(property.key)) {
      byKey.set(property.key, property);
    }
  }
  return [...byKey.values()];
};

const mergeFavoriteSetsByKey = (
  favoriteSets: readonly SplitDisplaySet[]
): SplitDisplaySet[] => {
  const mergedByKey = new Map<string, SplitDisplaySet>();

  for (const favoriteSet of favoriteSets) {
    const current = mergedByKey.get(favoriteSet.key);
    if (current === undefined) {
      mergedByKey.set(favoriteSet.key, {
        key: favoriteSet.key,
        name: favoriteSet.name,
        favoriteProperties: mergeUniqueProperties(
          favoriteSet.favoriteProperties
        ),
        nonFavoriteProperties: mergeUniqueProperties(
          favoriteSet.nonFavoriteProperties
        ),
      });
      continue;
    }

    mergedByKey.set(favoriteSet.key, {
      key: current.key,
      name: current.name,
      favoriteProperties: mergeUniqueProperties([
        ...current.favoriteProperties,
        ...favoriteSet.favoriteProperties,
      ]),
      nonFavoriteProperties: mergeUniqueProperties([
        ...current.nonFavoriteProperties,
        ...favoriteSet.nonFavoriteProperties,
      ]),
    });
  }

  return [...mergedByKey.values()];
};

export const normalizePanelData = (
  commonSets: readonly DisplaySet[],
  entitySets: readonly DisplayEntitySets[],
  favoriteKeys: ReadonlySet<string>,
  sortAlphabetically = false
): NormalizedPanelModel => {
  const flatSets: DisplaySet[] = [
    ...commonSets,
    ...entitySets.flatMap((entitySet) => entitySet.psets),
  ];
  // Alphabetical mode interleaves custom and model/common sets by name so a
  // custom set lands in its true sort position. In the default view custom
  // sets instead take visual priority and are pulled to the top.
  let allSets: DisplaySet[];
  if (sortAlphabetically) {
    allSets = [...flatSets].sort((a, b) =>
      comparePropertyNames(a.name, b.name)
    );
  } else {
    // Default view: custom sets first, then model/common (single pass).
    const customSets: DisplaySet[] = [];
    const modelSets: DisplaySet[] = [];
    for (const set of flatSets) {
      (set.isCustom ? customSets : modelSets).push(set);
    }
    allSets = [...customSets, ...modelSets];
  }
  const splitSets = splitSetsByFavorites(allSets, favoriteKeys);
  const favoriteSets = mergeFavoriteSetsByKey(splitSets.favorites);
  const regularSets = splitSets.regular;

  return {
    favoriteSets,
    favoriteSetKeys: favoriteSets.map((set) => set.key),
    regularSets,
    regularSetKeys: regularSets.map((set) => set.key),
  };
};
